import { useQuery } from "@apollo/client";
import { memo, useEffect } from "react";
import GetGatheringShareInfoDocument from "src/api/graphql/__generated__/documents/GetGatheringShareInfoDocument";
import GetPhotoForPhotoDetailLazyPhotoDocument from "src/api/graphql/__generated__/documents/GetPhotoForPhotoDetailLazyPhotoDocument";
import { GetPhotoForPhotoDetailLazyPhotoQueryVariables } from "src/api/graphql/__generated__/graphql";
import { useGatheringStore } from "src/contexts/GatheringContext";
import usePhotoDetailStore from "../stores/usePhotoDetailStore";

const PhotoDetailDataManagement = () => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	// Used by PhotoDetailShareFooter. We only want to make one network request in the hierarchy
	useQuery(GetGatheringShareInfoDocument, {
		variables: {
			input: {
				gatheringId,
			},
		},
		fetchPolicy: "cache-and-network",
	});

	const debouncedSettledPhoto = usePhotoDetailStore((state) => state.debouncedSettledPhoto);
	const { data, refetch } = useQuery(GetPhotoForPhotoDetailLazyPhotoDocument, {
		variables: {
			input: {
				photoId: debouncedSettledPhoto?.photoId ?? "",
				gatheringId,
			},
			reactionUsersInput: {
				limit: 4, // 1 extra than what we display to allow for wiggle room with own profile
				key: "Preview", // Keying this to avoid cache issues with the simultaneous queries
			},
		} satisfies GetPhotoForPhotoDetailLazyPhotoQueryVariables,
		skip: !debouncedSettledPhoto,
		fetchPolicy: "cache-and-network",
	});
	useEffect(() => {
		const { listenerTopic } = usePhotoDetailStore.getState();
		const { unsubscribe } = listenerTopic.on("refreshAsyncSettledPhoto", () => {
			refetch();
		});

		return () => {
			unsubscribe();
		};
	}, [refetch]);
	useEffect(() => {
		if (!debouncedSettledPhoto) {
			usePhotoDetailStore.setState({
				asyncSettledPhoto: null,
			});
		}
	}, [debouncedSettledPhoto]);
	useEffect(() => {
		const getPhoto = data?.getPhoto;
		if (!getPhoto) return;
		const { curPhoto, debouncedSettledPhoto } = usePhotoDetailStore.getState();

		// Checking for desync
		if (!curPhoto || curPhoto.photoId !== getPhoto.photoId) {
			return;
		}
		if (!debouncedSettledPhoto || debouncedSettledPhoto.photoId !== getPhoto.photoId) {
			return;
		}

		usePhotoDetailStore.setState({
			asyncSettledPhoto: getPhoto,
		});
	}, [data]);

	return null;
};

export default memo(PhotoDetailDataManagement);
